/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useState } from 'react';
import {
  InputAdornment, InputLabel, MenuItem, Select, TextField,
} from '@mui/material';
import { ExpandUpIcon } from 'src/icons';
import { CommonElementProps } from '../types';
import ElementUtils from '../utils';

export interface Option {
  currencyName: string;
  currencyIcon: string;
  id?: number;
  name?: string;
}

export interface InputProps<T, V> extends CommonElementProps<V> {
  label?: string;
  iconStart?: T;
  helperText?: string;
  type?: string;
  value?: V;
  disabled?: boolean;
  onChange?: (value: V) => void;
  onCurrencyChange?: (currency: Option) => void;
  placeholder?: string;
  currencyOptions?: Option[];
}

function Input<T, V>(props: InputProps<T, V>) {
  const {
    iconStart,
    label,
    type = 'string',
    helperText,
    value,
    disabled,
    onChange,
    error,
    onCurrencyChange,
    placeholder,
    currencyOptions = [],
  } = props;

  const handleValueChange = (event: any) => {
    if (!disabled && onChange) {
      onChange(event.target.value as V);
    }
  };

  const iconStartCurrency = currencyOptions.find(
    (option) => option.currencyIcon === iconStart,
  ) || currencyOptions[0];

  const [currency, setCurrency] = useState(iconStartCurrency || currencyOptions[0]);

  const handleCurrencyChange = (event: { target: { value: string } }) => {
    const selected = currencyOptions.find(
      (option) => option.currencyName === event.target.value,
    );
    if (!selected) {
      return;
    }
    setCurrency(selected);
    if (onCurrencyChange) {
      onCurrencyChange(selected);
    }
  };

  useEffect(() => {
    if (currencyOptions.length === 0) {
      return;
    }
    const matched = currencyOptions.find((option) => option.currencyIcon === iconStart)
      || currencyOptions.find((option) => option.currencyName === iconStart)
      || currencyOptions[0];
    setCurrency(matched);
  }, [iconStart, currencyOptions]);

  const currencySymbolFontSize = (currency?.currencyIcon?.trim().length ?? 0) > 1
    ? '12px'
    : '20px';
  const showCurrencySelector = currencyOptions.length > 0;

  return (
    <div className="flex flex-col justify-center items-start gap-1">
      {label && (
        <InputLabel className="font-bold text-[#28364C] fontSize-[1rem]" error={!!error}>
          {label}
        </InputLabel>
      )}

      <TextField
        placeholder={placeholder || label}
        id="outlined-basic"
        color="primary"
        defaultValue={value}
        value={value}
        type={type}
        variant="outlined"
        fullWidth
        helperText={helperText}
        disabled={disabled}
        onChange={handleValueChange}
        sx={{
          '& fieldset': { border: 'none' },
          '& .MuiInputBase-root': {
            flexWrap: 'nowrap',
          },
          '& .MuiInputBase-input': {
            minWidth: '56px',
            flex: 1,
          },
        }}
        error={!!error}
        InputProps={{
          startAdornment: showCurrencySelector ? (
            <InputAdornment position="start" sx={{ marginRight: 0, maxHeight: '2.5rem' }}>
              <div className="h-[2.5rem] flex items-center rounded-[0.625rem] shrink-0">
                <Select
                  value={currency?.currencyName || ''}
                  onChange={handleCurrencyChange}
                  disabled={disabled}
                  IconComponent={ExpandUpIcon}
                  renderValue={(selected) => selected}
                  sx={{
                    backgroundColor: '#F6F6F6',
                    borderTopLeftRadius: '4px',
                    borderBottomLeftRadius: '4px',
                    fontSize: '12px',
                    height: '2.5rem',
                    minWidth: '52px',
                    maxWidth: '52px',
                    '& .MuiSelect-select': {
                      padding: '8px 24px 8px 8px',
                      minHeight: 'unset',
                    },
                  }}
                  inputProps={{ 'aria-label': 'Currency' }}
                  classes={{
                    icon: 'mr-0 mb-2 w-3 h-3',
                  }}
                >
                  {currencyOptions.map((option) => (
                    <MenuItem
                      key={option.currencyName}
                      className="text-[#28364C]"
                      value={option.currencyName}
                    >
                      {option.currencyName}
                    </MenuItem>
                  ))}
                </Select>
                <div
                  className="flex-shrink-0 whitespace-nowrap text-center"
                  style={{
                    borderTopLeftRadius: '4px',
                    borderBottomLeftRadius: '4px',
                    padding: '10px 4px',
                    fontSize: currencySymbolFontSize,
                    minWidth: '40px',
                    maxWidth: '44px',
                  }}
                  data-testid="currency-icon"
                >
                  {currency?.currencyIcon}
                </div>
              </div>
            </InputAdornment>
          ) : null,
          classes: {
            root: disabled ? 'pl-0 text-[#28364C] rounded-md' : 'bg-white custom-shadow pl-0 rounded-md',
            error: 'outline outline-[1px] outline-error',
          },
        }}
      />
      {error && (
        <div className="text-error text-xs">
          {ElementUtils.renderError(error)}
        </div>
      )}
    </div>
  );
}

Input.defaultProps = {
  label: '',
  iconStart: '',
  placeholder: '',
  helperText: '',
  value: '',
  type: 'string',
  disabled: false,
  onChange: () => { },
  onCurrencyChange: () => { },
  currencyOptions: [],
};

export default Input;
